<template>
  <zm-select
    filterable
    :filter-method="filter"
    v-model="country"
    ref="select"
    :filter-label="filterLabel"
    label="Select Country"
    :clearable="clearable"
    popper-append-to-body
    :placeholder="placeholder"
    :disabled="disabled"
    @hook:mounted="onSelectMounted"
    @visible-change="onSelectVisibleChange"
  >
    <zm-option
      v-for="innerCountry in countries"
      :key="innerCountry.id"
      :label="innerCountry.text"
      :aria-label="innerCountry.text + ' +' + innerCountry.code"
      :value="innerCountry.id"
    >
      <div class="zm-country-select">
        <img v-if="showFlag && visible" class="zm-country-select__flag" :alt="'Flag of ' + innerCountry.text" :src="innerCountry.flag" />
        <span class="zm-country-select__text">{{innerCountry.text}}</span>
        <span class="zm-country-select__code">+{{innerCountry.code}}</span>
      </div>
    </zm-option>
    <span v-if="shouldShowPrefix()" slot="prefix" class="zm-country-select__prefix">
      <img class="zm-country-select__flag" :alt="'Flag of ' + country" :src="selectedFlag" />
    </span>
  </zm-select>
</template>

<script>
  import countryMap from './locale/country-map';
  import locale from '../../mixins/locale';
  import cloneDeep from 'lodash-es/cloneDeep';
  import { escapeRegexpString } from '../../utils/util';

  export default {
    name: 'ZmCountrySelect',
    mixins: [locale],
    props: {
      value: {
        type: [String, Object],
        default: ''
      },
      clearable: Boolean,
      disabled: Boolean,
      placeholder: {
        type: String,
        default: 'Select'
      },
      i18nKeyMap: Object,
      showFlag: Boolean,
      countryMap: Array
    },
    data() {
      return {
        country: typeof this.value !== 'string' ? this.value.id : this.value,
        showPrefix: false,
        query: '',
        withPlus: false,
        visible: false
      };
    },
    created() {
      this.valueType = typeof this.value !== 'string' ? 'object' : 'string';
    },
    computed: {
      countries() {
        const countries = [];
        const idMap = {};
        const map = this.countryMap || countryMap;
        cloneDeep(map).forEach((country) => {
          if (country.code && !idMap[country.id]) {
            idMap[country.id] = 1;
            country.text = this.getCountryText(country);
            if (this.showFlag) {
              country.flag = require(`./flags/${country.id}.png`);
            }
            if (this.match(country.text, country.code)) {
              countries.push(country);
            }
          }
        });
        return countries;
      },
      selectedFlag() {
        const country = this.findCountryById(this.country);
        return country ? country.flag : '';
      }
    },
    methods: {
      shouldShowPrefix() {
        return this.selectedFlag && (this.$refs.select && !this.$refs.select.visible);
      },
      onSelectMounted() {
        this.$forceUpdate();
      },
      onSelectVisibleChange(val) {
        this.visible = val;
        if (!val) {
          const country = this.findCountryById(this.country);
          if (country) {
            setTimeout(() => {
              this.$refs.select.query = '+' + country.code;
            }, 0);
          }
        }
      },
      filterLabel(text, id) {
        const country = this.findCountryById(id);
        return country ? '+' + country.code : text;
      },
      findCountryById(value) {
        return this.countries.filter(country => {
          return country.id === value;
        })[0];
      },
      filter(val) {
        if (!val) {
          this.query = '';
          return;
        }
        val = val.trim();
        if (val.indexOf('+') === 0) {
          this.withPlus = true;
          val = val.slice(1);
        } else {
          this.withPlus = false;
        }
        this.query = val;
      },
      getCountryText(country) {
        if (this.countryMap) {
          // No need get i18n country name if countryMap is got from props
          return country.text;
        }
        let i18nKeyMap = this.i18nKeyMap;
        if (!i18nKeyMap && typeof window.sctyName === 'string') {
          try {
            i18nKeyMap = JSON.parse(window.sctyName);
          } catch (e) {
          }
        }
        if (i18nKeyMap && i18nKeyMap[country.id]) {
          return this.t(i18nKeyMap[country.id]);
        } else {
          return this.t(country.text);
        }
      },
      match(text, code) {
        if (!this.query) {
          return true;
        }
        let query = this.query;
        if (this.withPlus) {
          query = '+' + query;
          code = '+' + code;
        }
        const reg = new RegExp(escapeRegexpString(query), 'i');
        return reg.test(text) || reg.test(code);
      }
    },
    watch: {
      country(newVal) {
        const { id, code } = this.findCountryById(newVal) || { id: '', code: '' };
        if (this.valueType === 'string') {
          this.$emit('input', id);
        } else {
          this.$emit('input', {
            id,
            code
          });
        }
      },
      value(newVal) {
        this.country = typeof newVal !== 'string' ? newVal.id : newVal;
      }
    }
  };
</script>

<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";

  @include b(country-select) {
    display: flex;
    align-items: center;

    @include e(flag) {
      width: 16px;
      height: 16px;
      margin-right: $--size-sm;
    }

    @include e(text) {
      flex: 1;
    }

    @include e(code) {
      margin: 0 $--size-sm;
    }

    @include e(prefix) {
      display: flex;
      min-width: 16px;
    }
  }
</style>
